import { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, Save, X, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";

const DEFAULT_PRICING = {
  base_a: { label: "1 human + 1 pet (with food)", price: 1499, enabled: true },
  base_b: { label: "Solo human (with food)", price: 899, enabled: true },
  addon_pet: { label: "Additional pet", price: 499, enabled: true },
  addon_human_no_food: { label: "Additional human (no food)", price: 699, enabled: true },
  addon_human_with_food: { label: "Additional human (with food)", price: 1199, enabled: true },
};

const emptyEvent = {
  title: "", date: "", time: "", location: "", description: "", long_description: "",
  cover_image: "", is_past: false, max_capacity: "",
  seats_note: "",
  pricing: { ...DEFAULT_PRICING },
};

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [up, past] = await Promise.all([api.get("/events?past=false"), api.get("/events?past=true")]);
    setEvents([...up.data, ...past.data]);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = {
        ...editing,
        max_capacity: editing.max_capacity ? Number(editing.max_capacity) : null,
      };
      // Normalise pricing lines to {label, price, enabled}
      const normPricing = {};
      const keys = ["base_a", "base_b", "addon_pet", "addon_human_no_food", "addon_human_with_food"];
      for (const k of keys) {
        const line = editing.pricing?.[k] || DEFAULT_PRICING[k];
        normPricing[k] = {
          label: (line.label ?? DEFAULT_PRICING[k].label) || DEFAULT_PRICING[k].label,
          price: Number(line.price ?? 0) || 0,
          enabled: line.enabled !== false,
        };
      }
      payload.pricing = normPricing;
      if (editing.id) {
        await api.put(`/admin/events/${editing.id}`, payload);
        toast.success("Event updated");
      } else {
        await api.post("/admin/events", payload);
        toast.success("Event created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/admin/events/${id}`);
    toast.success("Deleted");
    load();
  };

  const undoCover = async (id) => {
    try {
      await api.post(`/admin/events/${id}/undo-cover`);
      toast.success("Restored previous cover");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nothing to restore");
    }
  };

  return (
    <div data-testid="admin-events">
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="font-serif text-3xl text-brand-soft">Events</h1>
          <p className="text-sm text-brand-soft/60 mt-1">Manage upcoming and past events.</p>
        </div>
        <button onClick={() => setEditing({ ...emptyEvent })} className="btn-primary-furshala inline-flex items-center gap-2" data-testid="admin-event-new">
          <Plus className="w-4 h-4" /> New event
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {events.map((ev) => (
          <div key={ev.id} className="bg-white rounded-2xl border border-brand-maroon/10 overflow-hidden" data-testid={`admin-event-row-${ev.id}`}>
            <div className="flex">
              <div className="w-28 h-28 flex-shrink-0 bg-brand-ivory">
                {ev.cover_image && <img src={fileUrl(ev.cover_image)} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-serif text-lg text-brand-soft">{ev.title}</div>
                    <div className="text-xs text-brand-soft/60">{ev.date} · {ev.location}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${ev.is_past ? "bg-brand-olive/15 text-brand-olive" : "bg-brand-mustard/20 text-brand-maroon"}`}>{ev.is_past ? "past" : "upcoming"}</span>
                </div>
                <div className="mt-2 text-xs text-brand-soft/60">Bookings: {ev.bookings_count}{ev.max_capacity ? ` / ${ev.max_capacity}` : ""}</div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => setEditing({ ...emptyEvent, ...ev, max_capacity: ev.max_capacity || "" })} className="text-xs px-3 py-1.5 rounded-full bg-brand-ivory hover:bg-brand-maroon/10 inline-flex items-center gap-1" data-testid={`admin-event-edit-${ev.id}`}>
                    <Edit2 className="w-3 h-3" /> Edit
                  </button>
                  {ev.previous_cover_image && (
                    <button onClick={() => undoCover(ev.id)} className="text-xs px-3 py-1.5 rounded-full bg-brand-ivory hover:bg-brand-maroon/10 inline-flex items-center gap-1">
                      <Undo2 className="w-3 h-3" /> Undo cover
                    </button>
                  )}
                  <button onClick={() => remove(ev.id)} className="text-xs px-3 py-1.5 rounded-full bg-red-50 text-red-700 hover:bg-red-100 inline-flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-7 sm:p-10" onClick={(e) => e.stopPropagation()} data-testid="event-editor-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-brand-soft">{editing.id ? "Edit event" : "New event"}</h2>
              <button onClick={() => setEditing(null)} className="p-2"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <L label="Title"><I value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testId="ev-title" /></L>
              <L label="Date (YYYY-MM-DD)"><I type="date" value={editing.date} onChange={(v) => setEditing({ ...editing, date: v })} testId="ev-date" /></L>
              <L label="Time"><I value={editing.time} onChange={(v) => setEditing({ ...editing, time: v })} placeholder="10 AM – 1 PM" testId="ev-time" /></L>
              <L label="Location"><I value={editing.location} onChange={(v) => setEditing({ ...editing, location: v })} testId="ev-location" /></L>
              <L label="Short description" wide>
                <textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={2} className="w-full bg-brand-ivory rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mustard/40" />
              </L>
              <L label="Long description" wide>
                <textarea value={editing.long_description} onChange={(e) => setEditing({ ...editing, long_description: e.target.value })} rows={3} className="w-full bg-brand-ivory rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mustard/40" />
              </L>
              <div className="md:col-span-2">
                <ImageUploader label="Cover banner" recommended="(recommended 1200×600)" value={editing.cover_image} onChange={(v) => setEditing({ ...editing, cover_image: v })} />
              </div>
              <L label="Max capacity (hidden from booking form)"><I type="number" value={editing.max_capacity} onChange={(v) => setEditing({ ...editing, max_capacity: v })} testId="ev-cap" /></L>
              <L label="Seats note (shown publicly)"><I value={editing.seats_note} onChange={(v) => setEditing({ ...editing, seats_note: v })} placeholder="Last few seats left" testId="ev-seats-note" /></L>
              <L label="Mark as past event">
                <label className="flex items-center gap-2 text-sm py-2">
                  <input type="checkbox" checked={editing.is_past} onChange={(e) => setEditing({ ...editing, is_past: e.target.checked })} className="accent-brand-maroon" /> Past event
                </label>
              </L>
            </div>

            <div className="mt-7">
              <div className="font-serif text-lg text-brand-soft mb-3">Pricing</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  ["base_a", "Base A · 1 human + 1 pet (with food)"],
                  ["base_b", "Base B · Solo human (with food)"],
                  ["addon_pet", "Additional pet"],
                  ["addon_human_no_food", "Additional human (no food)"],
                  ["addon_human_with_food", "Additional human (with food)"],
                ].map(([k, hint]) => {
                  const line = editing.pricing?.[k] || { label: "", price: 0, enabled: true };
                  const updateLine = (patch) => setEditing({
                    ...editing,
                    pricing: { ...editing.pricing, [k]: { ...line, ...patch } },
                  });
                  return (
                    <div key={k} className="md:col-span-2 bg-brand-ivory rounded-xl p-4 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-xs uppercase tracking-widest text-brand-soft/60">{hint}</div>
                        <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                          <input
                            type="checkbox"
                            checked={line.enabled !== false}
                            onChange={(e) => updateLine({ enabled: e.target.checked })}
                            className="accent-brand-maroon w-4 h-4"
                            data-testid={`ev-price-${k}-enabled`}
                          />
                          <span className="text-brand-soft">{line.enabled !== false ? "Shown on form" : "Hidden"}</span>
                        </label>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <label className="sm:col-span-2 block">
                          <div className="text-[11px] uppercase tracking-widest text-brand-soft/50 mb-1">Label shown to customer</div>
                          <input
                            type="text"
                            value={line.label ?? ""}
                            onChange={(e) => updateLine({ label: e.target.value })}
                            placeholder="e.g. Solo human"
                            className="w-full bg-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mustard/40"
                            data-testid={`ev-price-${k}-label`}
                          />
                        </label>
                        <label className="block">
                          <div className="text-[11px] uppercase tracking-widest text-brand-soft/50 mb-1">Price ₹</div>
                          <input
                            type="number"
                            value={line.price ?? ""}
                            onChange={(e) => updateLine({ price: e.target.value })}
                            className="w-full bg-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mustard/40"
                            data-testid={`ev-price-${k}-price`}
                          />
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-8 flex gap-3 justify-end">
              <button onClick={() => setEditing(null)} className="btn-outline-furshala">Cancel</button>
              <button onClick={save} className="btn-primary-furshala inline-flex items-center gap-2" data-testid="ev-save">
                <Save className="w-4 h-4" /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const L = ({ label, children, wide }) => (
  <label className={`block space-y-1 ${wide ? "md:col-span-2" : ""}`}>
    <div className="text-xs uppercase tracking-widest text-brand-soft/60">{label}</div>
    {children}
  </label>
);

const I = ({ value, onChange, type = "text", placeholder, testId }) => (
  <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
    className="w-full bg-brand-ivory rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mustard/40"
    data-testid={testId}
  />
);
